import { purple } from '@mui/material/colors'
import { alpha } from '@mui/material/styles'

// ----------------------------------------------------------------------

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`
}

interface GradientsPaletteOptions {
  primary: string
  info: string
  success: string
  warning: string
  error: string
  purple: string
}

interface ChartPaletteOptions {
  violet: string[]
  blue: string[]
  green: string[]
  yellow: string[]
  red: string[]
}

interface GreenScale {
  50: string
  100: string
  300: string
  500: string
  600: string
  700: string
  800: string
  900: string
}

interface StatusPalette {
  ok: string
  okSoft: string
  info: string
  infoSoft: string
  warn: string
  warnSoft: string
  danger: string
  dangerSoft: string
}

interface ActionTokens {
  primaryBg: string
  primaryOn: string
  primaryHover: string
  primaryPressed: string
  tonalBg: string
  tonalHover: string
  tonalFg: string
}

declare module '@mui/material/styles/createPalette' {
  interface TypeAction extends ActionTokens {}
  interface TypeBackground {
    neutral: string
  }
  interface SimplePaletteColorOptions {
    lighter: string
    darker: string
  }
  interface PaletteColor {
    lighter: string
    darker: string
  }
  interface Palette {
    gradients: GradientsPaletteOptions
    chart: ChartPaletteOptions
    purple: PaletteColor
    green: GreenScale
    status: StatusPalette
  }
  interface PaletteOptions {
    green: GreenScale
    status: StatusPalette
    gradients: GradientsPaletteOptions
    chart: ChartPaletteOptions
    purple?: SimplePaletteColorOptions
  }
}

declare module '@mui/material' {
  interface Color {
    0: string
    500_8: string
    500_12: string
    500_16: string
    500_24: string
    500_32: string
    500_48: string
    500_56: string
    500_80: string
  }
}

// SETUP COLORS
const PRIMARY = {
  lighter: '#C8FACD',
  light: '#5BE584',
  main: '#00AB55',
  dark: '#007B55',
  darker: '#005249',
}
const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
}
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  background: '#E6F4FF',
  border: '#91CAFF',
}
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
}
const WARNING = {
  lighter: '#FEF3C7',
  light: '#FCD34D',
  main: '#F59E0B',
  dark: '#B45309',
  darker: '#78350F',
}
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
}

const PURPLE = {
  lighter: '#F3E8FF',
  light: '#D8B4FE',
  main: '#A855F7',
  dark: '#7E22CE',
  darker: '#581C87',
}

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
}

const GREEN = {
  50: '#EAF8EF',
  100: '#C8FACD',
  300: '#5BE584',
  500: '#00AB55',
  600: '#00843F',
  700: '#007B55',
  800: '#007A3D',
  900: '#005249',
}

const STATUS_LIGHT = {
  ok: '#007A3D',
  okSoft: '#EAF8EF',
  info: '#1D6AC8',
  infoSoft: '#E7F0FC',
  warn: '#9A6700',
  warnSoft: '#FFF6D6',
  danger: '#C62828',
  dangerSoft: '#FFEDEB',
}
const STATUS_DARK = {
  ok: '#5BE584',
  okSoft: '#12301F',
  info: '#6AA8F5',
  infoSoft: '#14263D',
  warn: '#F2C14E',
  warnSoft: '#33280E',
  danger: '#FF7B72',
  dangerSoft: '#3A1716',
}

const ACTION_TOKENS_LIGHT = {
  primaryBg: '#00843F',
  primaryOn: '#FFFFFF',
  primaryHover: '#007536',
  primaryPressed: '#00662F',
  tonalBg: '#EAF8EF',
  tonalHover: '#C8FACD',
  tonalFg: '#007A3D',
}
const ACTION_TOKENS_DARK = {
  primaryBg: '#1FBF6A',
  primaryOn: '#062012',
  primaryHover: '#3ACC7E',
  primaryPressed: '#16A85C',
  tonalBg: '#12301F',
  tonalHover: '#174A2C',
  tonalFg: '#5BE584',
}

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
  purple: createGradient(PURPLE.light, PURPLE.main),
}

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
}

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  purple: {...PURPLE, contrastText: '#fff'},
  grey: GREY,
  green: GREEN,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
}

const palette = {
  light: {
    ...COMMON,
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    status: STATUS_LIGHT,
    action: { active: GREY[600], ...COMMON.action, ...ACTION_TOKENS_LIGHT },
  },
  dark: {
    ...COMMON,
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    status: STATUS_DARK,
    action: { active: GREY[500], ...COMMON.action, ...ACTION_TOKENS_DARK },
  },
}

export default palette
